import React, { useCallback, useEffect, useRef, useState } from "react";

/** =========================
 * ✅ 超軽量トースト（共通）
 *   元は FiscalRakutenExpensePage / VariableCostSettingsPage に同じものがあった
 * ========================= */
export type ToastType = "success" | "error" | "info";
export type ToastItem = { id: string; type: ToastType; message: string };

function toastClass(t: ToastType) {
    if (t === "success") return "border-emerald-300 bg-emerald-50 text-emerald-900";
    if (t === "error") return "border-red-300 bg-red-50 text-red-900";
    return "border-slate-300 bg-slate-50 text-slate-900";
}

export function useToast() {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const toastTimersRef = useRef<Record<string, any>>({});

    const removeToast = useCallback((id: string) => {
        const t = toastTimersRef.current[id];
        if (t) {
            clearTimeout(t);
            delete toastTimersRef.current[id];
        }
        setToasts((prev) => prev.filter((x) => x.id !== id));
    }, []);

    /** timeoutMs が 0 以下なら自動では消さない（× で閉じるまで残す） */
    const pushToast = useCallback(
        (type: ToastType, message: string, timeoutMs = 3500) => {
            const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
            setToasts((prev) => [...prev, { id, type, message }]);
            if (timeoutMs > 0) {
                toastTimersRef.current[id] = setTimeout(() => removeToast(id), timeoutMs);
            }
        },
        [removeToast]
    );

    useEffect(() => {
        return () => {
            for (const id of Object.keys(toastTimersRef.current)) {
                clearTimeout(toastTimersRef.current[id]);
            }
            toastTimersRef.current = {};
        };
    }, []);

    return { toasts, pushToast, removeToast };
}

/**
 * ✅ トーストの表示枠（画面上部・中央）
 *   children には「未保存ヒント」など常駐させたい帯を渡す（トーストの上に並ぶ）
 */
export function ToastViewport({
    toasts,
    onClose,
    children,
}: {
    toasts: ToastItem[];
    onClose: (id: string) => void;
    children?: React.ReactNode;
}) {
    return (
        <div className="fixed top-16 left-0 right-0 md:left-[210px] z-[999] flex flex-col items-center gap-2 px-4 pointer-events-none">
            {children}

            {toasts.map((t) => (
                <div key={t.id} className="pointer-events-auto">
                    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 shadow ${toastClass(t.type)}`}>
                        <div className="text-xs whitespace-pre-line break-all max-h-[50vh] overflow-y-auto">{t.message}</div>
                        <button
                            className="ml-2 text-xs px-2 py-1 rounded border bg-white/70 hover:bg-white"
                            onClick={() => onClose(t.id)}
                            aria-label="閉じる"
                        >
                            ×
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}
